/**
 * Renders the coach's `choices` block as a row of buttons.
 *
 * Each option is one of:
 *  - a plain string → a RE-SEND button (tapping sends the label as the next
 *    message in this conversation; the original behavior);
 *  - a `DrillChoiceOption` with a canonical `drill_target` → a LAUNCH button
 *    (a billing-gated `DrillLaunchLink` that opens a fresh `/drill/new` session
 *    on that skill). This is how the coach routes a leak into a real drill
 *    instead of dead-looping on a re-sent label.
 *
 * Fail-closed: a drill option whose `drill_target` isn't a known canonical
 * skill id degrades to a re-send button — never a launch link that would 422
 * (the decision lives in `classifyOption`, in chatRoute). So the worst case
 * equals the original re-send behavior.
 *
 * Locking is applied at render: while `locked` (a later user message exists, or
 * a stream is in-flight) every option is a disabled button — no `DrillLaunchLink`
 * mounts, so we never navigate mid-stream or from a stale historical message.
 */

import type { ChoicesPayload } from "@/shared/models";
import { classifyOption } from "@/shared/chatRoute";
import { DrillLaunchLink } from "@/shared/components/DrillLaunchLink";
import { Target } from "@/shared/components/icons";

interface ChoicesBlockProps {
  choices: ChoicesPayload;
  locked: boolean;
  onChoice: (option: string) => void;
}

const BTN =
  "inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-lg border text-sm font-medium transition";
const BTN_ACTIVE =
  "border-border bg-surface hover:bg-accent/10 hover:border-accent active:bg-accent/15 active:border-accent/80 text-text";
const BTN_LOCKED = "border-border/50 text-text-muted cursor-not-allowed opacity-60";

export function ChoicesBlock({ choices, locked, onChoice }: ChoicesBlockProps) {
  return (
    <div className="flex flex-wrap gap-2">
      {choices.options.map((option, idx) => {
        const r = classifyOption(option);

        if (locked) {
          return (
            <button key={idx} type="button" disabled className={`${BTN} ${BTN_LOCKED}`}>
              {r.label}
            </button>
          );
        }

        if (r.kind === "launch") {
          return (
            <DrillLaunchLink key={idx} to={r.to} className={`${BTN} ${BTN_ACTIVE}`}>
              <Target size={14} strokeWidth={1.75} />
              {r.label}
            </DrillLaunchLink>
          );
        }

        return (
          <button
            key={idx}
            type="button"
            onClick={() => onChoice(r.label)}
            className={`${BTN} ${BTN_ACTIVE}`}
          >
            {r.label}
          </button>
        );
      })}
    </div>
  );
}
